<template>
  <BaseDialog
      :open="open"
      :disabled="creating"
      @close="$emit('close')"
  >
    <template #title>
      {{
        props?.notifParentId !== null && props?.notifParentId !== undefined ? 'Neue Korrekturmeldung' : 'Neue Meldung'
      }}
    </template>
    <form
        class="form"
        @submit.prevent="submit"
    >
      <!-- Betreff + Schüler (nebeneinander) -->
      <div
          class="field"
          style="display:grid;grid-template-columns:1fr 1fr;gap:8px;"
      >
        <div>
          <label for="f-student">Schüler</label>
          <select
              id="f-student"
              v-model.number="form.studentId"
              required
              class="input"
          >
            <option
                :value="null"
                disabled
            >
              Bitte wählen
            </option>
            <option
                v-for="s in studentOptions"
                :key="s.id"
                :value="s.id"
            >
              {{ s.name }}
            </option>
          </select>
          <p
              v-if="errors.studentId"
              class="help danger"
              role="alert"
          >
            {{ errors.studentId }}
          </p>
        </div>
        <div>
          <label for="f-subject">Betreff</label>
          <select
              id="f-subject"
              v-model="form.subject"
              class="input"
              required
          >
            <option
                value=""
                disabled
            >
              Bitte wählen
            </option>
            <option
                v-for="opt in subjectOptions"
                :key="opt"
                :value="opt"
            >
              {{ opt }}
            </option>
          </select>
          <p
              v-if="errors.subject"
              class="help danger"
              role="alert"
          >
            {{ errors.subject }}
          </p>
        </div>
      </div>

      <!-- Kommentar (optional) -->
      <div class="field">
        <label for="f-comment">Kommentar (optional)</label>
        <textarea
            id="f-comment"
            v-model.trim="form.comment"
            class="input"
            rows="3"
            placeholder="weitere Angaben"
        />
      </div>

      <!-- Gültigkeit: tageweise oder zeitweise -->
      <div class="field">
        <label>Gültigkeit</label>
        <div style="display:flex; gap:12px; align-items:center;">
          <label style="display:inline-flex; gap:6px; align-items:center;">
            <input
                v-model="form.kind"
                type="radio"
                name="validity"
                value="daily"
            >
            tageweise
          </label>
          <label style="display:inline-flex; gap:6px; align-items:center;">
            <input
                v-model="form.kind"
                type="radio"
                name="validity"
                value="time"
            >
            zeitweise
          </label>
        </div>
      </div>

      <!-- Zeitraum (tageweise: Von/Bis) -->
      <div
          v-if="form.kind === 'daily'"
          class="field"
          style="display:grid;grid-template-columns:1fr 1fr;gap:8px;"
      >
        <div>
          <label for="f-from">Von</label>
          <FlatPickr
              id="f-from"
              v-model="form.dateFrom"
              :config="dateConfig"
              class="input flatpickr-date"
              placeholder="TT.MM.JJJJ"
          />
        </div>
        <div>
          <label for="f-to">Bis</label>
          <FlatPickr
              id="f-to"
              v-model="form.dateTo"
              :config="endDateConfig"
              class="input flatpickr-date"
              placeholder="TT.MM.JJJJ"
          />
        </div>
      </div>

      <!-- Zeitraum (zeitweise: Datum + Uhrzeit Von/Bis) -->
      <div
          v-else
          class="field"
          style="display:grid;grid-template-columns:1fr 1fr 1fr;gap:8px;"
      >
        <div>
          <label for="f-date">Datum</label>
          <FlatPickr
              id="f-date"
              v-model="form.dateFrom"
              :config="dateSingleConfig"
              class="input flatpickr-date"
              placeholder="TT.MM.JJJJ"
          />
        </div>
        <div>
          <label for="f-time-from">Uhrzeit von</label>
          <FlatPickr
              id="f-time-from"
              v-model="form.timeFrom"
              :config="timeConfig"
              class="input flatpickr-time"
              placeholder="HH:MM"
          />
        </div>
        <div>
          <label for="f-time-to">Uhrzeit bis</label>
          <FlatPickr
              id="f-time-to"
              v-model="form.timeTo"
              :config="timeConfig"
              class="input flatpickr-time"
              placeholder="HH:MM"
          />
        </div>
      </div>

      <!-- Datei-Upload (Bilder/PDF) -->
      <div class="field">
        <label for="f-files">Anhang (Bilder/PDF)</label>
        <input
            id="f-files"
            type="file"
            multiple
            accept="image/*,application/pdf"
            class="input"
            @change="onFilesChange"
        >
        <small
            v-if="files.length"
            class="help"
        >Ausgewählt: {{ files.map(f => f.name).join(', ') }}</small>
      </div>

      <p
          v-if="error"
          class="notice notice-danger mt-12"
          role="alert"
      >
        {{ error }}
      </p>

      <p
          v-if="overlapInfo"
          class="notice mt-12"
          role="status"
      >
        {{ overlapInfo }}
      </p>

      <div
          class="mt-12"
          style="display:flex;gap:8px;justify-content:flex-end;"
      >
        <button
            type="button"
            class="btn"
            :disabled="creating"
            @click="onClose"
        >
          Abbrechen
        </button>
        <button
            type="submit"
            class="btn btn-primary"
            :disabled="creating"
        >
          <span
              v-if="creating"
              class="spinner"
              aria-hidden="true"
          /> Speichern
        </button>
      </div>
    </form>
  </BaseDialog>
</template>

<script setup>
import {ref, watch, computed, reactive} from 'vue';
import BaseDialog from '@/components/BaseDialog.vue';
import FlatPickr from 'vue-flatpickr-component';
import 'flatpickr/dist/flatpickr.css';
import {German} from 'flatpickr/dist/l10n/de.js';
import api from '@/services/api';
import {useAuthStore} from '@/stores/auth';

const props = defineProps({
  open: {type: Boolean, default: false},
  // optionale Vorbelegung
  prefillStudentId: {type: [Number, String], default: null},
  prefillSubject: {type: String, default: null},
  prefillDateFrom: {type: String, default: null},
  prefillDateTo: {type: String, default: null},
  prefillTimeFrom: {type: String, default: null},
  prefillTimeTo: {type: String, default: null},
  prefillKind: {type: String, default: null}, // 'daily' | 'time'
  // Bezug zur Eltern-Meldung (Korrektur, Thread-Bezug)
  notifParentId: {type: [Number, String], default: null}
});
const emit = defineEmits(['close', 'created']);

const auth = useAuthStore();
const roleId = computed(() => auth.user?.roleId ?? auth.user?.role_id ?? null);
const isParent = computed(() => roleId.value === 3);
const currentUserId = computed(() => auth.user?.id ?? null);
// Rollen-/Permissions-Logik für das Erstellen
const creating = ref(false);
const error = ref('');
const overlapInfo = ref('');
const files = ref([]);
const form = reactive({
  studentId: null,
  subject: '',
  comment: '',
  dateFrom: null,
  dateTo: null,
  status: 'offen',
  kind: 'daily',     // 'daily' | 'time'
  timeFrom: null,    // 'HH:MM'
  timeTo: null       // 'HH:MM'
});
const errors = reactive({studentId: '', subject: ''});

// Vordefinierte Betreff-Gründe
const subjectOptions = [
  'Krankmeldung',
  'Arzttermin',
  'Verspätung',
  'Familienangelegenheit',
  'Beurlaubung',
  'Sonstiges'
];

// FlatPickr Config (Datum)
const dateConfig = {
  dateFormat: 'Y-m-d',
  altInput: true,
  altFormat: 'd.m.Y',
  allowInput: true,
  locale: German
};
const endDateConfig = reactive({...dateConfig, minDate: null});
const dateSingleConfig = {...dateConfig}; // nur ein Datum
const timeConfig = {
  enableTime: true,
  noCalendar: true,
  dateFormat: 'H:i',
  time_24hr: true,
  allowInput: true,
  locale: German
};

watch(() => form.dateFrom, (val) => {
  // Enddatum-Minimum aktualisieren (tageweise)
  endDateConfig.minDate = val || null;
  // Bei zeitweise setzen wir dateTo immer gleich
  if (form.kind === 'time') {
    form.dateTo = val || null;
  }
});

// Kind-Wechsel: Felder sinnvoll synchronisieren
watch(() => form.kind, (k) => {
  error.value = '';
  if (k === 'daily') {
    // Zeiten zurücksetzen
    form.timeFrom = null;
    form.timeTo = null;
    // dateTo nicht zwangsweise gleichhalten
  } else {
    // 'time': Enddatum = Startdatum (sofern vorhanden)
    form.dateTo = form.dateFrom || null;
  }
});

// Schüler-Optionen laden (rolleabhängig)
const studentOptions = ref([]);

async function loadStudents() {
  try {
    if (isParent.value && currentUserId.value) {
      const {data} = await api.get(`/users/${encodeURIComponent(currentUserId.value)}/parent-students`);
      const list = data?.data ?? data ?? [];

      studentOptions.value = list.map(s => ({
        id: Number(s.student_id),
        name: String(s.name ?? (`${s.firstName ?? s.first_name ?? ''} ${s.lastName ?? s.last_name ?? ''}` || 'Schüler/in')).trim()
      }));
    } else {
      const {data} = await api.get('/students/all');
      const list = data?.items ?? data ?? [];
      studentOptions.value = list.map(s => ({
        id: Number(s.id),
        name: String(s.name ?? (`${s.firstName ?? s.first_name ?? ''} ${s.lastName ?? s.last_name ?? ''}` || 'Schüler/in')).trim()
      }));
    }
    // Alphabetisch sortieren (de)
    studentOptions.value.sort((a, b) => String(a.name).localeCompare(String(b.name), 'de', {sensitivity: 'base'}));
    // Bei nur einem Schüler: vorauswählen
    if (studentOptions.value.length === 1 && !form.studentId) {
      form.studentId = studentOptions.value[0].id;
    }
  } catch {
    studentOptions.value = [];
  }
}

function reset() {
  form.studentId = null;
  form.subject = '';
  form.comment = '';
  form.dateFrom = null;
  form.dateTo = null;
  form.status = 'offen';
  form.timeFrom = null;
  form.timeTo = null;
  errors.studentId = '';
  errors.subject = '';
  error.value = '';
  overlapInfo.value = '';
  creating.value = false;
  files.value = [];
}

watch(() => props.open, async (o) => {
  if (o) {
    reset();
    // Permissions für Lehrer laden (Admins/Eltern sind hiervon unabhängig)
    await loadStudents();

    // Prefill anwenden (optional)
    if (props.prefillKind) {
      form.kind = props.prefillKind === 'time' ? 'time' : 'daily';
    }

    if (props.prefillStudentId !== null && props.prefillStudentId !== undefined) {
      const n = Number(props.prefillStudentId);
      form.studentId = Number.isNaN(n) ? null : n;
    }
    if (typeof props.prefillSubject === 'string' && props.prefillSubject.trim()) {
      form.subject = props.prefillSubject.trim();
    }
    if (props.prefillDateFrom) form.dateFrom = props.prefillDateFrom;
    if (props.prefillDateTo) form.dateTo = props.prefillDateTo;
    if (props.prefillTimeFrom) {
      const m = String(props.prefillTimeFrom).trim().match(/^(\d{2}):(\d{2})/);
      form.timeFrom = m ? `${m[1]}:${m[2]}` : props.prefillTimeFrom;
    }
    if (props.prefillTimeTo) {
      const m2 = String(props.prefillTimeTo).trim().match(/^(\d{2}):(\d{2})/);
      form.timeTo = m2 ? `${m2[1]}:${m2[2]}` : props.prefillTimeTo;
    }

    // Konsistenz für 'time': date_to = date_from
    if (form.kind === 'time' && form.dateFrom && !form.dateTo) {
      form.dateTo = form.dateFrom;
    }
  }
});

function validate() {
  errors.studentId = '';
  errors.subject = '';
  error.value = '';
  let ok = true;
  if (!form.studentId) {
    errors.studentId = 'Bitte Schüler wählen.';
    ok = false;
  }
  if (!form.subject?.trim()) {
    errors.subject = 'Bitte Betreff angeben.';
    ok = false;
  }

  // Validierung abhängig von der Art
  if (form.kind === 'daily') {
    if (!form.dateFrom) {
      error.value = 'Bitte Datum wählen.';
      ok = false;
    }
    if (form.dateFrom && form.dateTo) {
      const a = new Date(form.dateFrom);
      const b = new Date(form.dateTo);
      if (!Number.isNaN(a.getTime()) && !Number.isNaN(b.getTime()) && b < a) {
        error.value = 'Bis-Datum darf nicht vor Von-Datum liegen.';
        ok = false;
      }
    }
  } else {
    // zeitweise: Datum erforderlich, Zeiten erforderlich, Enddatum == Startdatum
    if (!form.dateFrom) {
      error.value = 'Bitte Datum wählen.';
      ok = false;
    }
    // Spiegeln des Datums sicherstellen
    form.dateTo = form.dateFrom || null;

    // Zeiten prüfen (zuerst auf HH:MM kürzen)
    const toHm = (v) => {
      const m = String(v || '').trim().match(/^(\d{2}):(\d{2})/);
      return m ? `${m[1]}:${m[2]}` : String(v || '').trim();
    };
    const tf = toHm(form.timeFrom);
    const tt = toHm(form.timeTo);
    const timeRe = /^(?:[01]\d|2[0-3]):[0-5]\d$/;
    if (!tf || !timeRe.test(tf)) {
      error.value = 'Bitte gültige Uhrzeit (von) angeben.';
      ok = false;
    } else if (!tt || !timeRe.test(tt)) {
      error.value = 'Bitte gültige Uhrzeit (bis) angeben.';
      ok = false;
    } else {
      const [fH, fM] = tf.split(':').map(Number);
      const [tH, tM] = tt.split(':').map(Number);
      const fromMin = fH * 60 + fM;
      const toMin = tH * 60 + tM;
      if (toMin <= fromMin) {
        error.value = '„Uhrzeit bis“ muss nach „Uhrzeit von“ liegen.';
        ok = false;
      }
    }
  }
  return ok;
}

function onFilesChange(e) {
  files.value = Array.from(e?.target?.files || []);
}

function onClose() {
  emit('close');
}

/**
 * Prüft clientseitig auf Überschneidung mit bereits akzeptierten Meldungen
 * des gewählten Schülers und setzt eine neutrale Info-Notice, ohne zu blockieren.
 */
async function checkOverlapAndInform() {
  overlapInfo.value = '';

  const sid = form.studentId;
  if (!sid) return;

  // Kandidatenfenster aus Form bestimmen
  const toHms = (v) => {
    const m = String(v || '').trim().match(/^(\d{2}):(\d{2})(?::(\d{2}))?$/);
    return m ? `${m[1]}:${m[2]}:${m[3] ?? '00'}` : null;
  };

  let candStart = null;
  let candEnd = null;

  if (form.kind === 'daily') {
    if (!form.dateFrom) return;
    const df = String(form.dateFrom);
    const dt = String(form.dateTo || form.dateFrom);
    const start = new Date(`${df}T00:00:00`);
    const end = new Date(`${dt}T00:00:00`);
    end.setDate(end.getDate() + 1); // Ende exklusiv +1 Tag
    candStart = start.getTime();
    candEnd = end.getTime();
  } else {
    if (!form.dateFrom || !form.timeFrom || !form.timeTo) return;
    const hmsFrom = toHms(form.timeFrom);
    const hmsTo = toHms(form.timeTo);
    if (!hmsFrom || !hmsTo) return;
    const from = new Date(`${form.dateFrom}T${hmsFrom}`);
    let to = new Date(`${form.dateFrom}T${hmsTo}`);
    if (to.getTime() <= from.getTime()) {
      to.setDate(to.getDate() + 1); // über Mitternacht
    }
    candStart = from.getTime();
    candEnd = to.getTime();
  }

  if (candStart == null || candEnd == null || !(candEnd > candStart)) return;

  // Bestehende Meldungen laden und prüfen
  try {
    const res = await api.get('/notifications');
    const list = Array.isArray(res?.data) ? res.data
        : Array.isArray(res?.data?.items) ? res.data.items
            : [];

    const sameStudentAccepted = list.filter(n => {
      const nSid = n.studentId ?? n.student_id ?? null;
      const status = (n.status ?? '').toLowerCase();
      return String(nSid) === String(sid) && status === 'akzeptiert';
    });

    const overlap = sameStudentAccepted.some(n => {
      const kind = n.kind ?? (n.timeFrom || n.time_from || n.timeTo || n.time_to ? 'time' : 'daily');
      let s = null, e = null;
      if (kind === 'time') {
        const tf = n.timeFrom ?? n.time_from ?? null;
        const tt = n.timeTo ?? n.time_to ?? null;
        if (!tf || !tt) return false;
        const from = new Date(tf);
        const to = new Date(tt);
        s = from.getTime();
        e = to.getTime();
      } else {
        const df = n.dateFrom ?? n.date_from ?? null;
        const dt = n.dateTo ?? n.date_to ?? null;
        if (!df) return false;
        const start = new Date(`${df}T00:00:00`);
        const end = new Date(`${(dt || df)}T00:00:00`);
        end.setDate(end.getDate() + 1);
        s = start.getTime();
        e = end.getTime();
      }
      if (s == null || e == null) return false;
      return candStart < e && candEnd > s;
    });

    if (overlap) {
      overlapInfo.value = 'Hinweis: Es existieren bereits akzeptierte Meldungen im gewählten Zeitraum. Die neue Meldung wird trotzdem angelegt und später konsolidiert.';
    } else {
      overlapInfo.value = '';
    }
  } catch {
    // Bei Ladefehlern keine Blockade und keine Info setzen
  }
}

async function submit() {
  error.value = '';

  // Guard: verhindert Mehrfach-Klicks / mehrfaches Submit (z. B. Enter-Spam)
  if (creating.value) return;

  if (!validate()) return;

  // Sofort sperren – auch während checkOverlapAndInform() (Request) läuft
  creating.value = true;

  try {
    // Nicht-blockierende Überschneidungsprüfung (Info anzeigen, aber fortfahren)
    try {
      await checkOverlapAndInform();
    } catch {
      // still: Warnung nicht kritisch
    }

    const sel = studentOptions.value.find(s => s.id === form.studentId);
    const fd = new FormData();
    // Pflichtfelder
    fd.append('student_id', String(form.studentId));
    fd.append('subject', form.subject.trim());
    if (form.comment?.trim()) {
      fd.append('comment', form.comment.trim());
    }
    // Absender (user id)
    if (auth?.user?.id) {
      fd.append('parent_user_id', String(auth.user.id));
    }
    // Art der Meldung (optional fürs Backend)
    fd.append('kind', form.kind);

    // Zeitraum
    if (form.kind === 'daily') {
      if (form.dateFrom) fd.append('date_from', String(form.dateFrom));
      if (form.dateTo) fd.append('date_to', String(form.dateTo));
    } else {
      // zeitweise: date_to == date_from
      if (form.dateFrom) {
        fd.append('date_from', String(form.dateFrom));
        fd.append('date_to', String(form.dateFrom));
      }
      // Zeiten als UTC-ISO (analog Angebote) senden
      const toHms = (v) => {
        const m = String(v || '').trim().match(/^(\d{2}):(\d{2})(?::(\d{2}))?$/);
        return m ? `${m[1]}:${m[2]}:${m[3] ?? '00'}` : String(v || '').trim();
      };
      if (form.dateFrom && form.timeFrom) {
        const fromIso = new Date(`${form.dateFrom}T${toHms(form.timeFrom)}`).toISOString();
        fd.append('time_from', fromIso);
      }
      if (form.dateFrom && form.timeTo) {
        const toIso = new Date(`${form.dateFrom}T${toHms(form.timeTo)}`).toISOString();
        fd.append('time_to', toIso);
      }
    }

    // parent_id (Korrektur) mitsenden, falls vorhanden
    if (props.notifParentId !== null && props.notifParentId !== undefined) {
      fd.append('parent_id', String(props.notifParentId));
    }

    // Status (default offen)
    fd.append('status', 'offen');
    // Dateien anhängen
    for (const f of files.value) {
      fd.append('files[]', f);
    }

    const res = await api.post('/notifications', fd, {
      headers: {'Content-Type': 'multipart/form-data'}
    });
    const data = res?.data;
    if (data && (data.id || data.studentName)) {
      emit('created', data);
    } else {
      // Fallback falls Backend anders antwortet
      const fallback = {
        id: Date.now(),
        studentId: form.studentId,
        studentName: sel?.name ?? 'Schüler/in',
        subject: form.subject.trim(),
        comment: form.comment?.trim() || '',
        parentName: auth?.user?.id,
        dateFrom: form.dateFrom ? String(form.dateFrom) : null,
        dateTo: form.kind === 'time'
            ? (form.dateFrom ? String(form.dateFrom) : null)
            : (form.dateTo ? String(form.dateTo) : null),
        timeFrom: form.kind === 'time' ? (form.timeFrom || null) : null,
        timeTo: form.kind === 'time' ? (form.timeTo || null) : null,
        hasAttachment: files.value.length > 0,
        status: 'offen'
      };
      emit('created', fallback);
    }

    // Falls Korrekturmeldung: zugehörige Eltern-Meldung auf "abgelehnt" setzen (UI-seitige Absicherung)
    try {
      if (props.notifParentId !== null && props.notifParentId !== undefined) {
        await api.post(`/notifications/${encodeURIComponent(props.notifParentId)}/status`, {
          status: 'abgelehnt',
          reason: 'Korrekturmeldung wurde erstellt'
        });
      }
    } catch {
      // still: Backend kann dies ggf. serverseitig übernehmen; Fehler hier nicht blockierend
    }
  } catch (e) {
    error.value = e?.response?.data?.message || 'Konnte nicht angelegt werden.';
  } finally {
    creating.value = false;
  }
}
</script>
<style scoped>

.field {
  margin-bottom: 1rem;
}

:deep(footer) {
  display: none;
}
</style>